import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RichEditor } from "@/components/RichEditor";
import { ArrowLeft, Plus, Trash2, Upload, Loader2, ExternalLink, Star } from "lucide-react";

const inputCls = "w-full border-b border-[#27272A] bg-transparent py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white rounded-none";
const labelCls = "text-xs uppercase tracking-widest text-zinc-500";
const boxCls = "border border-white/10 bg-[#0a0a0a] p-6";
const CATS = [["engineering", "Engineering"], ["trading", "Trading"], ["school", "School 1–12"], ["mba", "MBA Finance"]];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

const blankLesson = () => ({ id: null, title: "", duration: "", video_url: "", free_preview: false, explanation: "", quiz: [], updated_at: "" });
const blankQuestion = () => ({ id: null, question: "", options: ["", "", "", ""], answer: 0 });
const blankQBank = () => ({ id: null, question: "", year: "", topic: "", answer: "", important: false });

export default function AdminEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [key] = useState(() => sessionStorage.getItem("pp_admin_key") || "");
  const headers = { "X-Admin-Key": key };
  const isNew = slug === "new";

  const [course, setCourse] = useState(null);
  const [details, setDetails] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [notesText, setNotesText] = useState("");
  const [qbank, setQbank] = useState([]);
  const [creating, setCreating] = useState({ title: "", category: "engineering", price: 999 });
  const [saving, setSaving] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!key || isNew) return;
    api.get("/admin/courses", { headers })
      .then((r) => {
        const c = r.data.find((x) => x.slug === slug);
        if (!c) {
          toast.error("Course not found.");
          navigate("/admin");
          return;
        }
        setCourse(c);
        setDetails({
          title: c.title,
          category: c.category,
          board: c.board || "",
          branch: c.branch || "",
          tagline: c.tagline || "",
          description: c.description || "",
          price: c.price,
          mrp: c.mrp,
          level: c.level || "",
          duration: c.duration || "",
          image: c.image || "",
          outcomesText: (c.outcomes || []).join("\n"),
          featuresText: (c.features || []).join("\n"),
        });
        setLessons((c.lessons || []).map((l) => ({ ...blankLesson(), ...l, quiz: (l.quiz || []).map((q) => ({ options: ["", "", "", ""], answer: 0, ...q })) })));
        setNotesText((c.notes || []).map((n) => [n.title, n.pdf_url || ""].join(" | ")).join("\n"));
        setQbank((c.question_bank || []).map((q) => ({ ...blankQBank(), ...q })));
      })
      .catch(() => {
        toast.error("Log in at the admin panel first.");
        navigate("/admin");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!key) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6" data-testid="editor-no-auth">
        <p className="font-display text-2xl font-bold">Admin login required.</p>
        <Link to="/admin" className="link-underline text-sm uppercase tracking-widest text-brand">Go to admin login</Link>
      </div>
    );
  }

  const setD = (k) => (e) => setDetails({ ...details, [k]: e.target.value });
  const setLesson = (i, patch) => setLessons((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const setQ = (li, qi, patch) => setLessons((ls) => ls.map((l, j) => (j === li ? { ...l, quiz: l.quiz.map((q, k) => (k === qi ? { ...q, ...patch } : q)) } : l)));
  const setQOpt = (li, qi, oi, val) => setLessons((ls) => ls.map((l, j) => (j === li ? { ...l, quiz: l.quiz.map((q, k) => (k === qi ? { ...q, options: q.options.map((o, m) => (m === oi ? val : o)) } : q)) } : l)));
  const setQB = (i, patch) => setQbank((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  const saveDetails = async () => {
    setSaving("details");
    try {
      await api.put(`/admin/courses/${slug}/details`, {
        title: details.title,
        category: details.category,
        board: details.category === "school" ? details.board : "",
        branch: details.category === "engineering" ? details.branch : "",
        tagline: details.tagline,
        description: details.description,
        price: Number(details.price) || 0,
        mrp: Number(details.mrp) || 0,
        level: details.level,
        duration: details.duration,
        image: details.image,
        outcomes: details.outcomesText.split("\n").map((s) => s.trim()).filter(Boolean),
        features: details.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
      }, { headers });
      toast.success("Course details saved.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving("");
    }
  };

  const saveContent = async () => {
    setSaving("content");
    try {
      const notes = notesText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
        const [title = "", pdf_url = ""] = l.split("|").map((s) => s.trim());
        return { title, pdf_url };
      });
      const cleanLessons = lessons
        .filter((l) => l.title.trim())
        .map((l) => ({
          ...l,
          quiz: l.quiz
            .filter((q) => q.question.trim())
            .map((q) => ({ ...q, options: q.options.map((o) => o.trim()), answer: Number(q.answer) || 0 })),
        }));
      await api.put(`/admin/courses/${slug}/content`, { lessons: cleanLessons, notes }, { headers });
      toast.success("Lessons and notes saved.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving("");
    }
  };

  const saveQbank = async () => {
    setSaving("qbank");
    try {
      await api.put(`/admin/courses/${slug}/question-bank`, qbank.filter((q) => q.question.trim()), { headers });
      toast.success("Question bank saved.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving("");
    }
  };

  const createCourse = async (e) => {
    e.preventDefault();
    setSaving("create");
    try {
      const { data } = await api.post("/admin/courses", { title: creating.title, category: creating.category, price: Number(creating.price) || 999 }, { headers });
      toast.success("Course created — now add its content.");
      navigate(`/admin/editor/${data.slug}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed.");
    } finally {
      setSaving("");
    }
  };

  const deleteCourse = async () => {
    if (!window.confirm(`Delete "${course.title}" permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/courses/${slug}`, { headers });
      toast.success("Course deleted.");
      navigate("/admin");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setNotesText((t) => (t ? `${t}\n` : "") + `${file.name.replace(/\.pdf$/i, "")} | ${url}`);
      toast.success("PDF uploaded — its link was added to the notes list.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (isNew) {
    return (
      <div className="min-h-screen px-6 py-12 md:px-12" data-testid="editor-new">
        <div className="mx-auto max-w-lg">
          <Link to="/admin" className="mb-10 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-zinc-500 hover:text-white">
            <ArrowLeft size={14} /> Admin
          </Link>
          <h1 className="font-display text-3xl font-black tracking-tighter">New Course<span className="text-brand">.</span></h1>
          <p className="mt-2 text-sm text-zinc-500">Create the course first — you'll add lessons, quizzes and the question bank right after.</p>
          <form onSubmit={createCourse} className={`${boxCls} mt-8 flex flex-col gap-6`}>
            <div>
              <label className={labelCls}>Course title</label>
              <input required data-testid="new-course-title" value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} placeholder="e.g. Fluid Mechanics" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select data-testid="new-course-category" value={creating.category} onChange={(e) => setCreating({ ...creating, category: e.target.value })} className={`${inputCls} cursor-pointer bg-[#0a0a0a]`}>
                {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Price (₹)</label>
              <input required type="number" min="1" data-testid="new-course-price" value={creating.price} onChange={(e) => setCreating({ ...creating, price: e.target.value })} className={inputCls} />
            </div>
            <button data-testid="new-course-submit" type="submit" disabled={saving === "create"} className="flex items-center justify-center gap-2 bg-brand px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 disabled:opacity-50">
              {saving === "create" && <Loader2 size={14} className="animate-spin" />} Create Course
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!details) {
    return <div className="flex min-h-screen items-center justify-center text-zinc-600" data-testid="editor-loading">Loading editor...</div>;
  }

  return (
    <div className="min-h-screen px-6 py-12 md:px-12" data-testid="admin-editor">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/admin" data-testid="editor-back-link" className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-zinc-500 hover:text-white">
            <ArrowLeft size={14} /> Admin
          </Link>
          <div className="flex items-center gap-3">
            <Link to={`/course/${slug}`} target="_blank" data-testid="view-as-student-link" className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-300 hover:border-brand hover:text-white">
              <ExternalLink size={13} /> View as Student
            </Link>
            <button onClick={deleteCourse} data-testid="delete-course-button" className="flex items-center gap-2 border border-red-500/40 px-4 py-2 text-xs font-bold uppercase tracking-widest text-red-400 transition-colors duration-300 hover:bg-red-500 hover:text-white">
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
        <h1 className="mt-6 font-display text-3xl font-black tracking-tighter" data-testid="editor-course-title">{course.title}<span className="text-brand">.</span></h1>

        <div className="mt-10 flex flex-col gap-10">
          <div className={boxCls} data-testid="details-section">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight">Course Details</h2>
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className={labelCls}>Title</label>
                <input data-testid="details-title" value={details.title} onChange={setD("title")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <select data-testid="details-category" value={details.category} onChange={setD("category")} className={`${inputCls} cursor-pointer bg-[#0a0a0a]`}>
                  {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              {details.category === "school" && (
                <div>
                  <label className={labelCls}>Board</label>
                  <select data-testid="details-board" value={details.board} onChange={setD("board")} className={`${inputCls} cursor-pointer bg-[#0a0a0a]`}>
                    <option value="">— Select board —</option>
                    <option value="gseb">GSEB (Gujarat Board)</option>
                    <option value="cbse">CBSE</option>
                    <option value="icse">ICSE</option>
                  </select>
                </div>
              )}
              {details.category === "engineering" && (
                <div>
                  <label className={labelCls}>Branch</label>
                  <select data-testid="details-branch" value={details.branch} onChange={setD("branch")} className={`${inputCls} cursor-pointer bg-[#0a0a0a]`}>
                    <option value="">— Select branch —</option>
                    <option value="common">Common (all branches)</option>
                    <option value="mechanical">Mechanical</option>
                    <option value="diploma">Mech Diploma (Sem 1–6)</option>
                    <option value="civil">Civil</option>
                    <option value="computer">Computer / IT</option>
                    <option value="electrical">Electrical</option>
                    <option value="ec">Electronics & Communication</option>
                    <option value="chemical">Chemical</option>
                  </select>
                </div>
              )}
              <div>
                <label className={labelCls}>Price (₹)</label>
                <input type="number" min="0" data-testid="details-price" value={details.price} onChange={setD("price")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>MRP / strike-through (₹)</label>
                <input type="number" min="0" data-testid="details-mrp" value={details.mrp} onChange={setD("mrp")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Level (e.g. Classes 9–10)</label>
                <input data-testid="details-level" value={details.level} onChange={setD("level")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Duration (e.g. 30+ hours)</label>
                <input data-testid="details-duration" value={details.duration} onChange={setD("duration")} className={inputCls} />
              </div>
            </div>
            <div className="mt-6 flex flex-col gap-6">
              <div>
                <label className={labelCls}>Tagline</label>
                <input data-testid="details-tagline" value={details.tagline} onChange={setD("tagline")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <textarea rows={3} data-testid="details-description" value={details.description} onChange={setD("description")} className={`${inputCls} resize-y`} />
              </div>
              <div>
                <label className={labelCls}>Image URL</label>
                <input data-testid="details-image" value={details.image} onChange={setD("image")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Outcomes — one per line</label>
                <textarea rows={4} data-testid="details-outcomes" value={details.outcomesText} onChange={setD("outcomesText")} className={`${inputCls} resize-y`} />
              </div>
              <div>
                <label className={labelCls}>Features — one per line</label>
                <textarea rows={3} data-testid="details-features" value={details.featuresText} onChange={setD("featuresText")} className={`${inputCls} resize-y`} />
              </div>
            </div>
            <button onClick={saveDetails} disabled={saving === "details"} data-testid="save-details-button" className="mt-6 flex items-center gap-2 bg-brand px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 disabled:opacity-50">
              {saving === "details" && <Loader2 size={14} className="animate-spin" />} Save Details
            </button>
          </div>

          <div className={boxCls} data-testid="lessons-section">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-display text-lg font-bold uppercase tracking-tight">Lessons & Explanations</h2>
                <p className="mt-1 text-xs text-zinc-500">Chapter titles as in the textbook · tick "free preview" for demo lessons · quiz at the end of each topic</p>
              </div>
              <button onClick={() => setLessons((ls) => [...ls, blankLesson()])} data-testid="add-lesson-button" className="flex items-center gap-2 border border-brand px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand transition-colors duration-300 hover:bg-brand hover:text-white">
                <Plus size={14} /> Add Lesson
              </button>
            </div>
            {lessons.map((l, i) => (
              <div key={i} className="mt-6 border border-white/10 p-5" data-testid={`lesson-editor-${i}`}>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Chapter {i + 1} · last saved {fmtDate(l.updated_at)}</span>
                  <button onClick={() => setLessons((ls) => ls.filter((_, j) => j !== i))} data-testid={`lesson-delete-${i}`} className="text-zinc-600 transition-colors duration-200 hover:text-red-400" aria-label="Delete lesson">
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                  <input placeholder="Chapter / topic title (as in the textbook)" value={l.title} onChange={(e) => setLesson(i, { title: e.target.value })} className={inputCls} data-testid={`lesson-title-${i}`} />
                  <input placeholder="Duration (e.g. 45 min)" value={l.duration} onChange={(e) => setLesson(i, { duration: e.target.value })} className={inputCls} data-testid={`lesson-duration-${i}`} />
                  <input placeholder="Video URL (YouTube, optional)" value={l.video_url} onChange={(e) => setLesson(i, { video_url: e.target.value })} className={`${inputCls} md:col-span-2`} data-testid={`lesson-video-${i}`} />
                </div>
                <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-zinc-300">
                  <input type="checkbox" checked={!!l.free_preview} onChange={(e) => setLesson(i, { free_preview: e.target.checked })} data-testid={`lesson-free-${i}`} className="h-4 w-4 accent-[#FF4500]" />
                  Free preview — anyone can open this lesson before buying
                </label>
                <div className="mt-4">
                  <label className={labelCls}>Simplified explanation</label>
                  <div className="mt-2">
                    <RichEditor value={l.explanation} onChange={(v) => setLesson(i, { explanation: v })} testId={`lesson-explanation-${i}`} />
                  </div>
                </div>
                <div className="mt-5">
                  <div className="flex items-center justify-between">
                    <label className={labelCls}>Self-test quiz</label>
                    <button onClick={() => setLesson(i, { quiz: [...l.quiz, blankQuestion()] })} data-testid={`quiz-add-${i}`} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand">
                      <Plus size={13} /> Add question
                    </button>
                  </div>
                  {l.quiz.map((q, qi) => (
                    <div key={qi} className="mt-3 border border-white/10 p-4" data-testid={`quiz-editor-${i}-${qi}`}>
                      <div className="flex items-center justify-between gap-4">
                        <input placeholder={`Question ${qi + 1}`} value={q.question} onChange={(e) => setQ(i, qi, { question: e.target.value })} className={inputCls} data-testid={`quiz-q-${i}-${qi}`} />
                        <button onClick={() => setLesson(i, { quiz: l.quiz.filter((_, k) => k !== qi) })} className="shrink-0 text-zinc-600 transition-colors duration-200 hover:text-red-400" aria-label="Delete question">
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {q.options.map((opt, oi) => (
                          <label key={oi} className="flex items-center gap-2">
                            <input type="radio" name={`correct-${i}-${qi}`} checked={Number(q.answer) === oi} onChange={() => setQ(i, qi, { answer: oi })} data-testid={`quiz-correct-${i}-${qi}-${oi}`} className="shrink-0 accent-[#FF4500]" />
                            <input placeholder={`Option ${oi + 1}`} value={opt} onChange={(e) => setQOpt(i, qi, oi, e.target.value)} className={inputCls} data-testid={`quiz-opt-${i}-${qi}-${oi}`} />
                          </label>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-zinc-600">The radio marks the correct option.</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {lessons.length === 0 && <p className="mt-6 text-sm text-zinc-500">No lessons yet — add the first chapter.</p>}
            <button onClick={saveContent} disabled={saving === "content"} data-testid="save-content-button" className="mt-6 flex items-center gap-2 bg-brand px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 disabled:opacity-50">
              {saving === "content" && <Loader2 size={14} className="animate-spin" />} Save Lessons & Notes
            </button>
          </div>

          <div className={boxCls} data-testid="notes-section">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight">PDF Notes</h2>
            <p className="mt-1 text-xs text-zinc-500">One per line: Title | PDF link — saved together with lessons above</p>
            <textarea rows={4} value={notesText} onChange={(e) => setNotesText(e.target.value)} data-testid="notes-textarea" className="mt-4 w-full resize-y border border-[#27272A] bg-transparent p-4 font-mono text-sm text-zinc-200 outline-none transition-colors duration-300 focus:border-white" />
            <label className="mt-4 inline-flex cursor-pointer items-center gap-2 border border-white/15 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-300 hover:border-brand hover:text-white">
              <Upload size={14} /> {uploading ? "Uploading..." : "Upload PDF"}
              <input data-testid="pdf-upload-input" type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} disabled={uploading} />
            </label>
          </div>

          <div className={boxCls} data-testid="qbank-section">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-display text-lg font-bold uppercase tracking-tight">Question Bank</h2>
                <p className="mt-1 text-xs text-zinc-500">Previous years' board / university questions · star the frequently repeated ones</p>
              </div>
              <button onClick={() => setQbank((qs) => [...qs, blankQBank()])} data-testid="add-qbank-button" className="flex items-center gap-2 border border-brand px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand transition-colors duration-300 hover:bg-brand hover:text-white">
                <Plus size={14} /> Add Question
              </button>
            </div>
            {qbank.map((q, i) => (
              <div key={i} className="mt-4 border border-white/10 p-4" data-testid={`qbank-editor-${i}`}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                  <input placeholder="Question (as it appeared in the exam)" value={q.question} onChange={(e) => setQB(i, { question: e.target.value })} className={`${inputCls} md:col-span-6`} data-testid={`qbank-q-${i}`} />
                  <input placeholder="Year" value={q.year} onChange={(e) => setQB(i, { year: e.target.value })} className={`${inputCls} md:col-span-2`} data-testid={`qbank-year-${i}`} />
                  <input placeholder="Topic" value={q.topic} onChange={(e) => setQB(i, { topic: e.target.value })} className={`${inputCls} md:col-span-3`} data-testid={`qbank-topic-${i}`} />
                  <div className="flex items-center gap-3 md:col-span-1">
                    <button onClick={() => setQB(i, { important: !q.important })} data-testid={`qbank-star-${i}`} className={`transition-colors duration-200 ${q.important ? "text-amber-400" : "text-zinc-600 hover:text-amber-400"}`} aria-label="Mark important">
                      <Star size={20} className={q.important ? "fill-amber-400" : ""} />
                    </button>
                    <button onClick={() => setQbank((qs) => qs.filter((_, j) => j !== i))} data-testid={`qbank-delete-${i}`} className="text-zinc-600 transition-colors duration-200 hover:text-red-400" aria-label="Delete question">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <input placeholder="Answer / solution hint (students see this after purchase)" value={q.answer} onChange={(e) => setQB(i, { answer: e.target.value })} className={`${inputCls} mt-3`} data-testid={`qbank-answer-${i}`} />
              </div>
            ))}
            {qbank.length === 0 && <p className="mt-4 text-sm text-zinc-500">No questions yet — add past board exam questions.</p>}
            <button onClick={saveQbank} disabled={saving === "qbank"} data-testid="save-qbank-button" className="mt-6 flex items-center gap-2 bg-brand px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 disabled:opacity-50">
              {saving === "qbank" && <Loader2 size={14} className="animate-spin" />} Save Question Bank
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
